import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Copy, Check, MessageCircle, Users } from "lucide-react";
import Particles from "@/components/Particles";
import BrandLogo from "@/components/BrandLogo";
import { TID } from "@/lib/testIds";
import { useSiteContent, useLinks } from "@/admin/SiteContent";

const SERVER_IP = "cfx.re/join/4gbraqq";
const DISCORD_TAG = "discord.gg/7y5VHubfxQ";

const CopyField = ({ label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = (e) => {
    e.preventDefault();
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <button
      onClick={copy}
      data-testid={testid}
      className="w-full text-left card-wow rounded-2xl px-5 py-4 flex items-center justify-between group"
    >
      <div>
        <p className="text-sm sm:text-base font-bold text-[#F5F5F5] tracking-wide">
          {value}
        </p>
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#B6B6BE] mt-1">
          {label}
        </p>
      </div>
      <span className="w-9 h-9 rounded-xl bg-[#0B0B0F]/60 border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#00D4FF]/60 transition-colors">
        {copied ? (
          <Check size={15} className="text-[#00D4FF]" />
        ) : (
          <Copy size={14} className="text-[#D4AF37]" />
        )}
      </span>
    </button>
  );
};

export const Hero = () => {
  const heroRef = useRef(null);
  const [t, setT] = useState(0);
  const { content } = useSiteContent();
  const h = content?.hero || {};
  const lk = useLinks();

  useEffect(() => {
    const onScroll = () => setT(Math.min(window.scrollY, 600));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="top"
      ref={heroRef}
      data-testid={TID.hero.root}
      className="relative min-h-[100vh] w-full overflow-hidden bg-hero-radial"
    >
      {/* Strong corner glows */}
      <div
        className="orb bg-[#D4AF37]"
        style={{ width: 600, height: 600, left: "-14%", top: "-12%", opacity: 0.22 }}
      />
      <div
        className="orb bg-[#00D4FF]"
        style={{
          width: 460,
          height: 460,
          right: "-10%",
          top: "30%",
          opacity: 0.16,
          animationDelay: "3s",
        }}
      />
      <div
        className="orb bg-[#D4AF37]"
        style={{
          width: 520,
          height: 520,
          right: "8%",
          bottom: "-18%",
          opacity: 0.18,
          animationDelay: "6s",
        }}
      />

      <Particles density={0.0001} />

      <div
        className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-40 sm:pt-48 pb-24"
        style={{ transform: `translate3d(0, ${t * -0.08}px, 0)` }}
      >
        {/* Eyebrow */}
        <div className="flex justify-center items-center gap-3 mb-10">
          <span className="pulse-dot" />
          <span className="font-mono-pl text-[11px] uppercase tracking-[0.34em] text-[#B6B6BE]">
            {h.badge || "FiveM · GTA V Roleplay · Italia"}
          </span>
        </div>

        {/* HERO — Huge wordmark logo */}
        <div className="flex justify-center">
          <BrandLogo size="xl" />
        </div>

        <p
          data-testid={TID.hero.subtitle}
          className="mt-12 mx-auto max-w-2xl text-center text-[#B6B6BE] text-base sm:text-lg leading-relaxed"
        >
          {h.subtitle ||
            "L'esperienza Roleplay definitiva su GTA V. Una community selezionata, scenari cinematografici e una città che vive 24/7."}
        </p>

        {/* Big CTA — central */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            data-testid={TID.hero.ctaJoin}
            href={lk.join}
            className="btn-gold inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm sm:text-base font-bold tracking-wider uppercase"
          >
            {h.cta_primary_label || "Entra ora su PureLife"}
            <ArrowUpRight size={18} strokeWidth={2.8} />
          </a>
          <a
            data-testid={TID.hero.ctaDiscord}
            href={lk.discord}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost-neon inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm sm:text-base font-semibold tracking-wide"
          >
            <MessageCircle size={16} />
            {h.cta_secondary_label || "Unisciti al Discord"}
          </a>
        </div>

        {/* Hidden title for testing */}
        <h1
          data-testid={TID.hero.title}
          className="sr-only"
        >
          PURELIFE Roleplay — GTA V FiveM
        </h1>

        {/* Floating info cards row — 5starsrp style */}
        <div className="mt-20 sm:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Big banner card */}
          <div className="lg:col-span-7 relative overflow-hidden rounded-3xl card-wow min-h-[280px] sm:min-h-[360px]">
            <img
              src="https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?auto=format&fit=crop&w=1600&q=75"
              alt="PureLife cinematic"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-70"
              style={{ filter: "contrast(1.05) saturate(0.9) brightness(0.6)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0B0F] via-[#0B0B0F]/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0B0B0F]/90" />
            <div className="relative h-full flex flex-col justify-end p-7 sm:p-10">
              <span className="inline-block w-fit font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3">
                In primo piano
              </span>
              <h3 className="font-display text-3xl sm:text-5xl uppercase text-[#F5F5F5] leading-[0.95] tracking-wide">
                Una città che <span className="text-gold glow-gold">non dorme mai</span>
              </h3>
              <p className="mt-3 text-[#B6B6BE] text-sm max-w-md">
                Eventi live, scenari cinematografici e un&apos;esperienza
                roleplay scolpita giorno per giorno dalla community.
              </p>
            </div>
          </div>

          {/* Side cards: server info + discord */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <CopyField
              label="Connessione FiveM diretta"
              value={SERVER_IP}
              testid="hero-server-ip"
            />
            <CopyField
              label="Server Discord ufficiale"
              value={DISCORD_TAG}
              testid="hero-discord-tag"
            />

            {/* Discord online count placeholder */}
            <div className="card-wow rounded-2xl p-5 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#B6B6BE]">
                  Online Discord
                </p>
                <p className="mt-1 font-display text-3xl text-[#F5F5F5]">
                  <span className="text-[#F5F5F5]/30">—</span>
                </p>
                <p className="text-[10px] text-[#B6B6BE] mt-0.5">
                  Dati in sincronizzazione
                </p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-[#0B0B0F]/60 border border-[#00D4FF]/30 flex items-center justify-center">
                <Users size={20} className="text-[#00D4FF]" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-b from-transparent to-[#0B0B0F] pointer-events-none" />
    </section>
  );
};

export default Hero;
